import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import PageSEO from "@/components/feature/PageSEO";

type Category = "all" | "engine" | "suspension" | "collision" | "maintenance";
type Brand = "all" | "Mercedes-Benz" | "BMW" | "Audi" | "VW" | "Porsche" | "Jaguar" | "Land Rover";

interface OEEntry {
  oeNumber: string;
  description: string;
  brand: string;
  models: string;
  years: string;
  tier: "Genuine OE" | "Premium Aftermarket";
  category: Category;
}

const categoryTabs: { id: Category; label: string; icon: string; skus: string }[] = [
  { id: "all", label: "All Categories", icon: "ri-apps-line", skus: "15,000+" },
  { id: "engine", label: "Engine & Powertrain", icon: "ri-settings-3-line", skus: "4,200+" },
  { id: "suspension", label: "Suspension & Chassis", icon: "ri-car-line", skus: "3,800+" },
  { id: "collision", label: "Collision & Exterior", icon: "ri-tools-line", skus: "5,100+" },
  { id: "maintenance", label: "Maintenance", icon: "ri-drop-line", skus: "2,900+" },
];

const brandTabs: { id: Brand; label: string; flag?: string }[] = [
  { id: "all", label: "All Brands" },
  { id: "Mercedes-Benz", label: "Mercedes-Benz" },
  { id: "BMW", label: "BMW" },
  { id: "Audi", label: "Audi" },
  { id: "VW", label: "Volkswagen" },
  { id: "Porsche", label: "Porsche" },
  { id: "Jaguar", label: "Jaguar" },
  { id: "Land Rover", label: "Land Rover" },
];

const oeData: OEEntry[] = [
  // Engine
  { oeNumber: "11287628650", description: "Engine Timing Chain Kit", brand: "BMW", models: "3 Series / 5 Series / X3 / X5", years: "2006–2016", tier: "Genuine OE", category: "engine" },
  { oeNumber: "A2720100705", description: "Engine Timing Chain Tensioner", brand: "Mercedes-Benz", models: "C-Class / E-Class / GLK", years: "2007–2015", tier: "Genuine OE", category: "engine" },
  { oeNumber: "06H103600", description: "Engine Oil Separator / PCV Valve", brand: "Audi", models: "A4 / A5 / A6 / Passat / Tiguan", years: "2008–2018", tier: "Premium Aftermarket", category: "engine" },
  { oeNumber: "059145701G", description: "Turbocharger Assembly", brand: "Audi", models: "A4 / A6 / Q5 / Q7 3.0 TDI", years: "2009–2016", tier: "Genuine OE", category: "engine" },
  { oeNumber: "271 050 33 47", description: "Engine Camshaft Adjuster Solenoid", brand: "Mercedes-Benz", models: "C180 / C200 / E200 / SLK", years: "2004–2014", tier: "Premium Aftermarket", category: "engine" },
  { oeNumber: "13717582312", description: "Air Filter Housing Assembly", brand: "BMW", models: "1 Series / 3 Series N47 Diesel", years: "2007–2013", tier: "Premium Aftermarket", category: "engine" },
  { oeNumber: "03L906461B", description: "EGR Valve Assembly", brand: "VW", models: "Golf / Passat / A3 / Octavia 2.0 TDI", years: "2008–2015", tier: "Genuine OE", category: "engine" },
  { oeNumber: "A0001401878", description: "Fuel Injector — Common Rail", brand: "Mercedes-Benz", models: "E220 / E250 CDI / C220 CDI", years: "2009–2016", tier: "Genuine OE", category: "engine" },
  { oeNumber: "11657583860", description: "Intake Manifold Flap Motor", brand: "BMW", models: "5 Series / 7 Series / X5 N57", years: "2010–2018", tier: "Premium Aftermarket", category: "engine" },
  { oeNumber: "A2760700501", description: "Engine Valve Cover Gasket Set", brand: "Mercedes-Benz", models: "E350 / ML350 / GL350 3.5L V6", years: "2006–2014", tier: "Premium Aftermarket", category: "engine" },
  { oeNumber: "95810905100", description: "Engine Mount — Right", brand: "Porsche", models: "Cayenne 958 / Panamera 970", years: "2010–2017", tier: "Genuine OE", category: "engine" },
  { oeNumber: "LR006285", description: "Supercharger Pulley Kit", brand: "Land Rover", models: "Range Rover 5.0 V8 SC / Sport", years: "2010–2018", tier: "Premium Aftermarket", category: "engine" },
  // Suspension
  { oeNumber: "8K0505311H", description: "Front Lower Control Arm — Left", brand: "Audi", models: "A4 B8 / A5 / Q5", years: "2008–2016", tier: "Genuine OE", category: "suspension" },
  { oeNumber: "4H0407151A", description: "Front Upper Control Arm", brand: "Audi", models: "A8 D4 / A8L", years: "2010–2018", tier: "Premium Aftermarket", category: "suspension" },
  { oeNumber: "1K0615301AA", description: "Front Brake Disc — Vented", brand: "VW", models: "Golf VI / Jetta / A3 / Octavia", years: "2008–2014", tier: "Genuine OE", category: "suspension" },
  { oeNumber: "WHT005549", description: "Front Wheel Hub Bearing Assembly", brand: "VW", models: "Passat B6 / B7 / CC / A4 B8", years: "2005–2015", tier: "Premium Aftermarket", category: "suspension" },
  { oeNumber: "A2213200438", description: "Rear Air Suspension Strut", brand: "Mercedes-Benz", models: "S-Class W221 / CL-Class", years: "2005–2013", tier: "Genuine OE", category: "suspension" },
  { oeNumber: "37126796929", description: "Front Air Spring Strut", brand: "BMW", models: "7 Series F01 / F02", years: "2008–2015", tier: "Genuine OE", category: "suspension" },
  { oeNumber: "LR023964", description: "Front Upper Ball Joint", brand: "Land Rover", models: "Discovery 4 / Range Rover Sport", years: "2009–2016", tier: "Premium Aftermarket", category: "suspension" },
  { oeNumber: "C2D5609", description: "Rear Subframe Bushing Kit", brand: "Jaguar", models: "XF X250 / XJ X351", years: "2008–2015", tier: "Premium Aftermarket", category: "suspension" },
  { oeNumber: "7L0698151B", description: "Front Brake Pad Set — OE Spec", brand: "Porsche", models: "Cayenne / Touareg 3.0 TDI", years: "2007–2014", tier: "Genuine OE", category: "suspension" },
  { oeNumber: "N0138157", description: "Stabilizer Bar Link — Front", brand: "Porsche", models: "911 / Boxster / Cayman", years: "2004–2012", tier: "Premium Aftermarket", category: "suspension" },
  { oeNumber: "C2P25592", description: "Rear Shock Absorber — Adaptive", brand: "Jaguar", models: "XJ X351 / XF X260", years: "2010–2018", tier: "Genuine OE", category: "suspension" },
  // Collision
  { oeNumber: "A2128800105", description: "Front Bumper Cover — Primed", brand: "Mercedes-Benz", models: "E-Class W212 Facelift", years: "2013–2016", tier: "Premium Aftermarket", category: "collision" },
  { oeNumber: "51117200697", description: "Front Bumper Grille — M-Sport", brand: "BMW", models: "3 Series F30 M-Sport", years: "2012–2018", tier: "Premium Aftermarket", category: "collision" },
  { oeNumber: "8K0853651", description: "Front Grille Frame — Singleframe", brand: "Audi", models: "A4 B8 Facelift", years: "2012–2015", tier: "Premium Aftermarket", category: "collision" },
  { oeNumber: "LR038157", description: "Front Wing / Fender — Left", brand: "Land Rover", models: "Range Rover Evoque L538", years: "2011–2018", tier: "Premium Aftermarket", category: "collision" },
  { oeNumber: "C2P24134", description: "Rear Bumper Cover Assembly", brand: "Jaguar", models: "XF X250 Facelift", years: "2011–2015", tier: "Premium Aftermarket", category: "collision" },
  { oeNumber: "95850505500", description: "Front Hood / Bonnet — Steel", brand: "Porsche", models: "Cayenne 958", years: "2010–2014", tier: "Premium Aftermarket", category: "collision" },
  { oeNumber: "A2126300023", description: "Headlight Lens Cover — Left", brand: "Mercedes-Benz", models: "E-Class W212", years: "2009–2013", tier: "Premium Aftermarket", category: "collision" },
  { oeNumber: "63117182519", description: "Headlight Assembly — Adaptive", brand: "BMW", models: "5 Series F10 / F11", years: "2010–2016", tier: "Genuine OE", category: "collision" },
  { oeNumber: "5K0807221", description: "Front Bumper Reinforcement Bar", brand: "VW", models: "Golf VI / Jetta VI", years: "2008–2013", tier: "Premium Aftermarket", category: "collision" },
  { oeNumber: "A2126300124", description: "Rear Tail Light Assembly — Left", brand: "Mercedes-Benz", models: "E-Class W212 Sedan", years: "2009–2013", tier: "Premium Aftermarket", category: "collision" },
  // Maintenance
  { oeNumber: "A0001401878B", description: "Engine Oil Filter — OE Spec", brand: "Mercedes-Benz", models: "C / E / S-Class OM651 Diesel", years: "2008–2018", tier: "Genuine OE", category: "maintenance" },
  { oeNumber: "11427953129", description: "Engine Oil Filter Housing Kit", brand: "BMW", models: "1 / 3 / 5 Series N47 / N57", years: "2007–2016", tier: "Premium Aftermarket", category: "maintenance" },
  { oeNumber: "1K0129620", description: "Air Filter Element", brand: "VW", models: "Golf / Passat / A3 / Octavia 2.0 TDI", years: "2004–2013", tier: "Premium Aftermarket", category: "maintenance" },
  { oeNumber: "A0000901051", description: "Cabin Air Filter — Activated Carbon", brand: "Mercedes-Benz", models: "C / E / GLC / GLE Class", years: "2014–2022", tier: "Genuine OE", category: "maintenance" },
  { oeNumber: "101905626", description: "Spark Plug — Iridium OE", brand: "Porsche", models: "Cayenne 3.6 V6 / Panamera", years: "2008–2016", tier: "Genuine OE", category: "maintenance" },
  { oeNumber: "12120037244", description: "Spark Plug — Double Platinum", brand: "BMW", models: "3 / 5 / 7 Series N52 / N54", years: "2006–2013", tier: "Premium Aftermarket", category: "maintenance" },
  { oeNumber: "A0004212910", description: "Brake Fluid DOT 4 — 1L", brand: "Mercedes-Benz", models: "All Models", years: "Universal", tier: "Genuine OE", category: "maintenance" },
  { oeNumber: "G052145A2", description: "DSG Transmission Fluid — 1L", brand: "VW", models: "All DSG 7-Speed Models", years: "2008–2020", tier: "Genuine OE", category: "maintenance" },
  { oeNumber: "LR023965", description: "Fuel Filter — Diesel In-Line", brand: "Land Rover", models: "Discovery / Range Rover 3.0 TDV6", years: "2009–2016", tier: "Premium Aftermarket", category: "maintenance" },
  { oeNumber: "WHT006019", description: "Timing Belt Kit — Complete", brand: "VW", models: "2.0 TDI CR Engine Family", years: "2008–2015", tier: "Premium Aftermarket", category: "maintenance" },
  { oeNumber: "C2S47168", description: "Supercharger Oil — 75ml", brand: "Jaguar", models: "XF / XJ 5.0 V8 SC", years: "2009–2015", tier: "Genuine OE", category: "maintenance" },
];

const tierColors: Record<string, { bg: string; text: string; border: string }> = {
  "Genuine OE": { bg: "rgba(0,217,255,0.08)", text: "#00D9FF", border: "rgba(0,217,255,0.3)" },
  "Premium Aftermarket": { bg: "rgba(255,180,0,0.08)", text: "#FFB400", border: "rgba(255,180,0,0.3)" },
};

// Map URL brand param → display brand
const brandParamMap: Record<string, Brand> = {
  mercedes: "Mercedes-Benz",
  bmw: "BMW",
  audi: "Audi",
  volkswagen: "VW",
  vw: "VW",
  porsche: "Porsche",
  jaguar: "Jaguar",
  landrover: "Land Rover",
};

const SITE_URL = import.meta.env.VITE_SITE_URL || "https://chirunnexa.com";

const catalogJsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "European OE Parts Catalog — 15,000+ SKUs | Chirunnexa",
    "url": `${SITE_URL}/catalog`,
    "description": "OE cross-reference database with 15,000+ SKUs for Mercedes-Benz, BMW, Audi, VW, Porsche, Jaguar, Land Rover. Engine, suspension, collision, maintenance parts. Genuine OE and premium aftermarket. B2B wholesale from Guangzhou.",
    "inLanguage": "en",
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": `${SITE_URL}/` },
        { "@type": "ListItem", "position": 2, "name": "OE Parts Catalog", "item": `${SITE_URL}/catalog` },
      ],
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "DataCatalog",
    "name": "Chirunnexa European OE Parts Cross-Reference Database",
    "description": "15,000+ SKU OE cross-reference database for European vehicles: Mercedes-Benz, BMW, Audi, Volkswagen, Porsche, Jaguar, Land Rover. Categories: Engine & Powertrain, Suspension & Chassis, Collision & Exterior, Maintenance.",
    "url": `${SITE_URL}/catalog`,
    "provider": {
      "@type": "Organization",
      "name": "Chirunnexa",
      "url": `${SITE_URL}/`,
    },
    "about": [
      { "@type": "Thing", "name": "Mercedes-Benz OE Parts" },
      { "@type": "Thing", "name": "BMW OE Parts" },
      { "@type": "Thing", "name": "Audi OE Parts" },
      { "@type": "Thing", "name": "Volkswagen OE Parts" },
      { "@type": "Thing", "name": "Porsche OE Parts" },
      { "@type": "Thing", "name": "Jaguar OE Parts" },
      { "@type": "Thing", "name": "Land Rover OE Parts" },
    ],
  },
];

export default function CatalogPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const initSearch = searchParams.get("search") || "";
  const initBrand = brandParamMap[searchParams.get("brand") || ""] || "all";

  const [activeCategory, setActiveCategory] = useState<Category>("all");
  const [activeBrand, setActiveBrand] = useState<Brand>(initBrand);
  const [search, setSearch] = useState(initSearch);

  // Sync URL params on mount
  useEffect(() => {
    const s = searchParams.get("search") || "";
    const b = brandParamMap[searchParams.get("brand") || ""] || "all";
    setSearch(s);
    setActiveBrand(b);
  }, [searchParams]);

  const filtered = oeData.filter((e) => {
    const matchCat = activeCategory === "all" || e.category === activeCategory;
    const matchBrand = activeBrand === "all" || e.brand === activeBrand;
    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      e.oeNumber.toLowerCase().includes(q) ||
      e.description.toLowerCase().includes(q) ||
      e.brand.toLowerCase().includes(q) ||
      e.models.toLowerCase().includes(q);
    return matchCat && matchBrand && matchSearch;
  });

  return (
    <div style={{ background: "#020810", minHeight: "100vh" }}>
      <PageSEO
        title="European OE Parts Catalog — 15,000+ SKUs | Mercedes BMW Audi | Chirunnexa"
        description="OE cross-reference database with 15,000+ SKUs for Mercedes-Benz, BMW, Audi, VW, Porsche, Jaguar, Land Rover. Engine, suspension, collision, maintenance parts. Genuine OE and premium aftermarket. B2B wholesale from Guangzhou."
        keywords="European OE parts catalog, Mercedes-Benz OE parts wholesale, BMW OE parts China, Audi OE parts supplier, VW parts wholesale, Porsche parts B2B, Jaguar Land Rover parts China"
        canonical="/catalog"
        jsonLd={catalogJsonLd}
      />
      {/* Top nav */}
      <div
        className="sticky top-0 z-40 px-6 h-14 flex items-center justify-between"
        style={{ background: "rgba(2,8,16,0.97)", borderBottom: "1px solid rgba(0,217,255,0.08)", backdropFilter: "blur(12px)" }}
      >
        <button
          onClick={() => navigate("/")}
          className="flex items-center gap-2 text-xs uppercase tracking-widest cursor-pointer transition-colors duration-200 whitespace-nowrap"
          style={{ color: "rgba(143,168,200,0.6)", letterSpacing: "0.14em" }}
          onMouseEnter={(e) => (e.currentTarget.style.color = "#00D9FF")}
          onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(143,168,200,0.6)")}
        >
          <i className="ri-arrow-left-line text-sm" />
          CHIRUNNEXA
        </button>
        <span className="text-xs uppercase tracking-widest font-light" style={{ color: "rgba(143,168,200,0.35)", letterSpacing: "0.2em" }}>
          OE Parts Catalog
        </span>
        <a
          href="/#contact"
          className="px-4 py-1.5 rounded-lg text-xs font-bold tracking-widest whitespace-nowrap cursor-pointer transition-all hover:brightness-110"
          style={{ background: "linear-gradient(135deg, #00D9FF 0%, #0099CC 100%)", color: "#020810", letterSpacing: "0.12em" }}
        >
          GET QUOTE
        </a>
      </div>

      {/* Header */}
      <div
        className="relative py-16 px-6 text-center overflow-hidden"
        style={{ borderBottom: "1px solid rgba(0,217,255,0.07)" }}
      >
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: "linear-gradient(rgba(0,217,255,0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(0,217,255,0.02) 1px, transparent 1px)",
            backgroundSize: "72px 72px",
          }}
        />
        <div
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-5"
          style={{ background: "rgba(0,217,255,0.06)", border: "1px solid rgba(0,217,255,0.2)" }}
        >
          <i className="ri-database-2-line text-xs" style={{ color: "#00D9FF" }} />
          <span className="text-xs tracking-widest uppercase font-light" style={{ color: "#00D9FF", letterSpacing: "0.2em" }}>
            OE Cross-Reference Database · 15,000+ SKUs
          </span>
        </div>
        <h1 className="text-3xl md:text-5xl font-thin text-white mb-4" style={{ letterSpacing: "0.05em" }}>
          EUROPEAN VEHICLE
          <br />
          <span style={{ color: "#00D9FF" }}>PARTS CATALOG.</span>
        </h1>
        <p className="text-sm font-light max-w-xl mx-auto" style={{ color: "#7a9bbf", lineHeight: "1.85" }}>
          Mercedes-Benz · BMW · Audi · Volkswagen · Porsche · Jaguar · Land Rover
          <br />
          Engine · Suspension · Collision · Maintenance — 2004 to 2024
        </p>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-10">

        {/* ── Search bar ── */}
        <div
          className="flex items-center gap-3 px-5 py-3.5 rounded-2xl mb-8"
          style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(0,217,255,0.18)" }}
        >
          <i className="ri-search-line text-base shrink-0" style={{ color: "#00D9FF" }} />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by OE number, description, brand, model..."
            className="bg-transparent text-white placeholder-gray-600 outline-none text-sm flex-1"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              className="shrink-0 cursor-pointer"
              style={{ color: "rgba(143,168,200,0.4)" }}
            >
              <i className="ri-close-line text-base" />
            </button>
          )}
        </div>

        {/* ── Brand filter ── */}
        <div className="mb-5">
          <p className="text-xs tracking-widest uppercase font-semibold mb-3" style={{ color: "rgba(143,168,200,0.4)", letterSpacing: "0.18em", fontSize: "9px" }}>
            Filter by Brand
          </p>
          <div className="flex flex-wrap gap-2">
            {brandTabs.map((b) => (
              <button
                key={b.id}
                onClick={() => setActiveBrand(b.id)}
                className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold tracking-wide transition-all duration-200 cursor-pointer whitespace-nowrap"
                style={{
                  background: activeBrand === b.id ? "rgba(0,217,255,0.12)" : "rgba(255,255,255,0.03)",
                  border: activeBrand === b.id ? "1px solid rgba(0,217,255,0.45)" : "1px solid rgba(255,255,255,0.06)",
                  color: activeBrand === b.id ? "#00D9FF" : "rgba(143,168,200,0.55)",
                }}
              >
                {b.label}
              </button>
            ))}
          </div>
        </div>

        {/* ── Category filter ── */}
        <div className="mb-8">
          <p className="text-xs tracking-widest uppercase font-semibold mb-3" style={{ color: "rgba(143,168,200,0.4)", letterSpacing: "0.18em", fontSize: "9px" }}>
            Filter by Category
          </p>
          <div className="flex flex-wrap gap-2">
            {categoryTabs.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-semibold tracking-wide transition-all duration-200 cursor-pointer whitespace-nowrap"
                style={{
                  background: activeCategory === cat.id ? "rgba(245,158,11,0.12)" : "rgba(255,255,255,0.03)",
                  border: activeCategory === cat.id ? "1px solid rgba(245,158,11,0.45)" : "1px solid rgba(255,255,255,0.06)",
                  color: activeCategory === cat.id ? "#F59E0B" : "rgba(143,168,200,0.55)",
                }}
              >
                <i className={`${cat.icon} text-sm`} />
                <span>{cat.label}</span>
                <span
                  className="px-1.5 py-0.5 rounded-full"
                  style={{
                    background: activeCategory === cat.id ? "rgba(245,158,11,0.15)" : "rgba(255,255,255,0.05)",
                    color: activeCategory === cat.id ? "#F59E0B" : "rgba(143,168,200,0.35)",
                    fontSize: "9px",
                  }}
                >
                  {cat.skus}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Results count */}
        <div className="flex items-center justify-between mb-4">
          <p className="text-xs font-light" style={{ color: "rgba(143,168,200,0.45)" }}>
            Showing <strong className="text-white">{filtered.length}</strong> results
            {activeBrand !== "all" && <span style={{ color: "#00D9FF" }}> · {activeBrand}</span>}
            {activeCategory !== "all" && <span style={{ color: "#F59E0B" }}> · {categoryTabs.find(c => c.id === activeCategory)?.label}</span>}
            {search && <span style={{ color: "rgba(143,168,200,0.6)" }}> · &quot;{search}&quot;</span>}
          </p>
          {/* Tier legend */}
          <div className="flex items-center gap-4">
            {Object.entries(tierColors).map(([tier, colors]) => (
              <div key={tier} className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full" style={{ background: colors.text }} />
                <span className="text-xs font-light" style={{ color: "rgba(143,168,200,0.5)", fontSize: "10px" }}>{tier}</span>
              </div>
            ))}
          </div>
        </div>

        {/* OE Table */}
        <div className="rounded-2xl overflow-hidden" style={{ border: "1px solid rgba(0,217,255,0.1)" }}>
          {/* Table header */}
          <div
            className="grid grid-cols-12 gap-4 px-6 py-3 text-xs uppercase tracking-widest font-semibold"
            style={{
              background: "rgba(0,217,255,0.05)",
              borderBottom: "1px solid rgba(0,217,255,0.1)",
              color: "rgba(143,168,200,0.5)",
              letterSpacing: "0.12em",
              fontSize: "9px",
            }}
          >
            <div className="col-span-2">OE Number</div>
            <div className="col-span-3">Description</div>
            <div className="col-span-2">Brand</div>
            <div className="col-span-3">Models</div>
            <div className="col-span-1">Years</div>
            <div className="col-span-1">Tier</div>
          </div>

          {/* Table rows */}
          {filtered.map((entry, i) => {
            const tc = tierColors[entry.tier];
            return (
              <div
                key={entry.oeNumber + i}
                className="grid grid-cols-12 gap-4 px-6 py-4 transition-all duration-200"
                style={{
                  borderBottom: i < filtered.length - 1 ? "1px solid rgba(255,255,255,0.04)" : "none",
                  background: "transparent",
                }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.background = "rgba(0,217,255,0.03)"; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = "transparent"; }}
              >
                <div className="col-span-2">
                  <span className="font-mono text-xs font-semibold" style={{ color: "#00D9FF", letterSpacing: "0.05em" }}>
                    {entry.oeNumber}
                  </span>
                </div>
                <div className="col-span-3">
                  <span className="text-xs text-white font-light">{entry.description}</span>
                </div>
                <div className="col-span-2">
                  <span className="text-xs font-semibold" style={{ color: "rgba(200,220,240,0.8)" }}>{entry.brand}</span>
                </div>
                <div className="col-span-3">
                  <span className="text-xs font-light" style={{ color: "rgba(143,168,200,0.6)" }}>{entry.models}</span>
                </div>
                <div className="col-span-1">
                  <span className="text-xs font-light" style={{ color: "rgba(143,168,200,0.5)" }}>{entry.years}</span>
                </div>
                <div className="col-span-1">
                  <span
                    className="px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap"
                    style={{ background: tc.bg, color: tc.text, border: `1px solid ${tc.border}`, fontSize: "9px" }}
                  >
                    {entry.tier === "Genuine OE" ? "OE" : "PM"}
                  </span>
                </div>
              </div>
            );
          })}

          {filtered.length === 0 && (
            <div className="py-16 text-center">
              <i className="ri-search-line text-3xl mb-4 block" style={{ color: "rgba(0,217,255,0.3)" }} />
              <p className="text-sm font-light mb-2" style={{ color: "rgba(143,168,200,0.4)" }}>
                No results found.
              </p>
              <p className="text-xs font-light" style={{ color: "rgba(143,168,200,0.3)" }}>
                Try a different OE number, brand, or category.
              </p>
            </div>
          )}
        </div>

        {/* CTA */}
        <div
          className="mt-12 p-8 rounded-2xl text-center"
          style={{ background: "rgba(0,217,255,0.04)", border: "1px solid rgba(0,217,255,0.12)" }}
        >
          <p className="text-sm font-light mb-2" style={{ color: "#7a9bbf" }}>
            Don&apos;t see your OE number? Our database covers <strong className="text-white">15,000+ active SKUs</strong> across all European brands.
          </p>
          <p className="text-xs font-light mb-6" style={{ color: "rgba(143,168,200,0.45)" }}>
            Submit your full parts list for a comprehensive quote within 24 hours.
          </p>
          <a
            href="/#contact"
            className="inline-flex items-center gap-2 px-8 py-3 rounded-xl text-xs font-bold tracking-widest transition-all hover:brightness-110 whitespace-nowrap cursor-pointer"
            style={{ background: "linear-gradient(135deg, #00D9FF 0%, #0099CC 100%)", color: "#020810", letterSpacing: "0.14em" }}
          >
            <i className="ri-mail-send-line text-sm" />
            SUBMIT PARTS LIST
          </a>
        </div>
      </div>
    </div>
  );
}
